import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import AuthLayout from '../../components/Auth/AuthLayout';
import LoadingButton from '../../components/Auth/LoadingButton';
import TwoFactorMethodModal from '../../components/Auth/TwoFactorMethodModal/TwoFactorMethodModal';
import api from '../../services/api';
import { useAppDispatch } from '../../store/hooks';
import { setCredentials } from '../../store/slices/authSlice';

const TwoFactorVerifyPage = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    const [twofa_email, setTwofa_email] = useState('');
    const [questions, setQuestions] = useState([]);
    const [answer, setAnswer] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showMethodModal, setShowMethodModal] = useState(false);
    const [verificationMethod, setVerificationMethod] = useState('security_questions');
    
    // Use ref to track if we've already sent OTP to prevent double calls
    const otpSentRef = useRef(false);

    const sendOtp = async (method, contact, email) => {
        try {
            const response = await api.post('/2fa/send-otp', {
                email: email,
                method: method,
                contact: contact
            });
            if (response.success) {
                toast.success(`Verification code sent via ${method}`);
            }
        } catch (err) {
            toast.error(`Failed to send verification code via ${method}`);
        }
    };

    // Load 2FA data on mount
    useEffect(() => {
        const email = sessionStorage.getItem('twofa_email');
        const questionsData = sessionStorage.getItem('twofa_questions');
        const method = searchParams.get('method') || 'security_questions';

        if (!email) {
            toast.error('Session expired. Please login again.');
            navigate('/');
            return;
        }

        setTwofa_email(email);
        setVerificationMethod(method);

        if (method === 'security_questions' && questionsData) {
            setQuestions(JSON.parse(questionsData));
        } else if (method === 'email' && !otpSentRef.current) {
            console.log(email)
            otpSentRef.current = true;
            sendOtp('email', email, email);
        } else if (method === 'sms' && !otpSentRef.current) {
            otpSentRef.current = true;
            const phone = sessionStorage.getItem('twofa_phone') || '';
            sendOtp('sms', phone, email);
        }
    }, [navigate]);

    // Update URL when verification method changes
    useEffect(() => {
        const currentMethod = searchParams.get('method');
        if (currentMethod !== verificationMethod && twofa_email) {
            navigate(`/2fa-verify?method=${verificationMethod}`, { replace: true });
        }
    }, [verificationMethod, searchParams, navigate, twofa_email]);

    const currentQuestion = questions[0];

    const lockIcon = <i className="fa fa-key fa-lg text-jet-black-400"></i>;

    const handleAnswerChange = (e) => {
        setAnswer(e.target.value);
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (verificationMethod === 'security_questions') {
            if (!answer.trim()) {
                setError('Please enter your answer');
                return;
            }
        } else {
            if (!answer.trim() || answer.length !== 6) {
                setError('Please enter the complete 6-digit code');
                return;
            }
        }

        setLoading(true);
        setError('');

        try {
            let response;

            if (verificationMethod === 'security_questions') {
                const currentQuestion = questions[0];
                response = await api.post('/2fa/verify-security-questions', {
                    email: twofa_email,
                    qId: currentQuestion.id,
                    answer: answer
                });
            } else {
                response = await api.post('/2fa/verify-otp', {
                    email: twofa_email,
                    method: verificationMethod,
                    otp: answer
                });
            }

            if (response.success) {
                const { user, token, refreshToken } = response.data;

                localStorage.setItem('token', token);
                localStorage.setItem('refreshToken', refreshToken || '');
                localStorage.setItem('user', JSON.stringify(user));

                dispatch(
                    setCredentials({
                        user,
                        token,
                        refreshToken
                    })
                );

                sessionStorage.removeItem('twofa_email');
                sessionStorage.removeItem('twofa_password');
                sessionStorage.removeItem('twofa_questions');
                sessionStorage.removeItem('twofa_phone');

                toast.success('Login successful!');

                // Determine redirect URL based on user type
                let redirectUrl = '/jetsetter/profile/user-details'; // Default for JetSetter
                if (user.user_type === 'Admin' || user.user_type === 'SubAdmin') {
                    redirectUrl = '/admin';
                } else if (user.user_type === 'Airliner') {
                    redirectUrl = '/airliner/profile';
                }

                setTimeout(() => {
                    navigate(redirectUrl);
                }, 100);
            } else {
                setError(response.message || 'Verification failed. Please try again.');
            }
        } catch (err) {
            setError(err.message || 'Verification failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectMethod = (method) => {
        setVerificationMethod(method);
        setAnswer('');
        setError('');
        setShowMethodModal(false);
        otpSentRef.current = false;

        if (method === 'email') {
            sendOtp('email', twofa_email, twofa_email);
        } else if (method === 'sms') {
            const phone = sessionStorage.getItem('twofa_phone') || '';
            sendOtp('sms', phone, twofa_email);
        } else if (method === 'security_questions') {
            // Fetch a fresh random security question
            fetchNewSecurityQuestion();
        }
    };

    const fetchNewSecurityQuestion = async () => {
        try {
            const response = await api.get(`/2fa/security-questions?email=${encodeURIComponent(twofa_email)}`);
            if (response.success && response.data) {
                setQuestions(response.data);
            } else {
                toast.error('Failed to load security question');
            }
        } catch (err) {
            toast.error('Failed to load security question');
        }
    };

    if (!currentQuestion && verificationMethod === 'security_questions') {
        return (
            <AuthLayout
                title="Loading..."
                subtitle="Please wait while we load your security questions"
            >
                <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jet-yellow"></div>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            title={verificationMethod === 'security_questions' ? "Two Factor Authentication" : 'Verification Code'}
        >
            <form onSubmit={handleSubmit} className="space-y-3">

                {verificationMethod === 'security_questions' && (
                    <div>
                        <p className="text-jet-gray-600 font-semibold text-sm m-0 mb-3">
                            {currentQuestion?.question || 'Security Question'}
                        </p>

                        <div className="relative">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none">
                                {lockIcon}
                            </div>
                            <input
                                type="password"
                                value={answer}
                                onChange={handleAnswerChange}
                                placeholder="Answer"
                                disabled={loading}
                                autoFocus
                                className="w-full pl-10 pr-4 py-3 border border-jet-gray-300 rounded-lg text-jet-gray-900 font-medium text-sm bg-white transition-colors duration-200 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]"
                            />
                        </div>

                        {error && <p className="text-sm text-jet-red-600 mt-2">{error}</p>}
                    </div>
                )}

                {(verificationMethod === 'email' || verificationMethod === 'sms') && (
                    <div>
                        <p className="text-jet-gray-600 font-semibold text-sm m-0 mb-3">
                            {verificationMethod === 'email'
                                ? `Enter the Verification Code sent to your registered email`
                                : `We have just sent a one time password over to your registered Mobile Number`}
                        </p>

                        <input
                            type="text"
                            value={answer}
                            onChange={handleAnswerChange}
                            placeholder="Enter OTP"
                            disabled={loading}
                            autoFocus
                            maxLength="6"
                            className="w-full px-4 py-3 border border-jet-gray-300 rounded-lg text-jet-gray-900 font-medium text-sm bg-white transition-colors duration-200 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]"
                        />

                        {error && <p className="text-sm text-jet-red-600 text-center mb-2">{error}</p>}
                    </div>
                )}

                <div className='flex justify-end'>
                    <button
                        type="button"
                        onClick={() => setShowMethodModal(true)}
                        className="text-jet-gray-400 hover:text-jet-gray-600 text-sm transition-colors"
                    >
                        Try Another Way
                    </button>
                </div>

                <LoadingButton
                    type="submit"
                    loading={loading}
                    loadingText="Verifying..."
                >
                    Submit
                </LoadingButton>

            </form>

            <TwoFactorMethodModal
                isOpen={showMethodModal}
                currentMethod={verificationMethod}
                onSelectMethod={handleSelectMethod}
                onClose={() => setShowMethodModal(false)}
            />
        </AuthLayout>
    );
};

export default TwoFactorVerifyPage;
